// Simple ELO-style rating system for 4-player Mario Kart races
// Pure functions with strict math as specified

const ELO_CONFIG = {
  MIN_RATING: 200,
  MAX_RATING: 1000,
  DEFAULT_RATING: 500,
  BASE_DIVISOR: 300,        // Increased sensitivity (was 400)
  K_OPPONENT: 6,            // Stronger opponent influence (was 4)
  MEAN_REVERSION_ALPHA: 0.0025  // Slightly stronger mean reversion (was 0.002)
};

/**
 * Calculate base placement delta
 * @param {number} place - 1st, 2nd, 3rd, or 4th
 * @returns {number} Base delta points
 */
function getBasePlacementDelta(place) {
  switch (place) {
    case 1: return 6;   // +6 (unchanged)
    case 2: return 3;   // +3 (was +2)
    case 3: return -3;  // -3 (was -2)
    case 4: return -6;  // -6 (unchanged)
    default: throw new Error(`Invalid placement: ${place}`);
  }
}

/**
 * Convert placement to score value
 * @param {number} place - 1st, 2nd, 3rd, or 4th
 * @returns {number} Score between 0 and 1
 */
function placementToScore(place) {
  return (4 - place) / 3;
}

/**
 * Calculate expected performance against opponents
 * @param {number} playerRating - Player's current rating
 * @param {number} avgOpponentRating - Average rating of 3 opponents
 * @returns {number} Expected performance (0-1)
 */
function calculateExpectedPerformance(playerRating, avgOpponentRating) {
  const ratingDiff = avgOpponentRating - playerRating;
  return 1 / (1 + Math.pow(10, ratingDiff / ELO_CONFIG.BASE_DIVISOR));
}

/**
 * Calculate opponent strength bonus
 * @param {number} place - Player's placement (1-4)
 * @param {number} playerRating - Player's current rating
 * @param {number} avgOpponentRating - Average rating of 3 opponents
 * @returns {number} Opponent bonus points
 */
function calculateOpponentBonus(place, playerRating, avgOpponentRating) {
  const actualScore = placementToScore(place);
  const expectedScore = calculateExpectedPerformance(playerRating, avgOpponentRating);
  return ELO_CONFIG.K_OPPONENT * (actualScore - expectedScore);
}

/**
 * Calculate mean reversion component (only for players who have raced)
 * @param {number} rating - Current rating
 * @param {boolean} hasRaced - Whether player has participated in races
 * @returns {number} Mean reversion delta
 */
function calculateMeanReversion(rating, hasRaced = true) {
  // Only apply mean reversion to players who have actually raced
  if (!hasRaced) return 0;
  return ELO_CONFIG.MEAN_REVERSION_ALPHA * (ELO_CONFIG.DEFAULT_RATING - rating);
}

/**
 * Calculate edge scaling factor (more damping at edges)
 * @param {number} rating - Current rating
 * @returns {number} Scaling factor (0.5 to 1.0)
 */
function calculateEdgeScaling(rating) {
  const distanceFromCenter = Math.abs(rating - ELO_CONFIG.DEFAULT_RATING);
  const scaling = 1 - 0.5 * (distanceFromCenter / ELO_CONFIG.DEFAULT_RATING);
  return Math.max(0.5, Math.min(1.0, scaling));
}

/**
 * Clamp rating to valid range
 * @param {number} rating - Rating to clamp
 * @returns {number} Clamped rating
 */
function clampRating(rating) {
  return Math.max(ELO_CONFIG.MIN_RATING, Math.min(ELO_CONFIG.MAX_RATING, rating));
}

/**
 * Update all player ratings based on race results
 * @param {string[]} players - Array of player names
 * @param {number[]} ratings - Array of current ratings (same order as players)
 * @param {number[]} placements - Array of placements 1-4 (same order as players)
 * @returns {number[]} New ratings array
 */
export function updateAllRatings(players, ratings, placements) {
  if (players.length !== 4 || ratings.length !== 4 || placements.length !== 4) {
    throw new Error('Must have exactly 4 players, ratings, and placements');
  }

  // Validate placements are 1, 2, 3, 4
  const sortedPlacements = [...placements].sort();
  if (JSON.stringify(sortedPlacements) !== JSON.stringify([1, 2, 3, 4])) {
    throw new Error('Placements must be exactly [1, 2, 3, 4] in some order');
  }

  const newRatings = [];

  for (let i = 0; i < 4; i++) {
    const playerRating = ratings[i];
    const playerPlace = placements[i];
    
    // Calculate average opponent rating (exclude current player)
    const opponentRatings = ratings.filter((_, index) => index !== i);
    const avgOpponentRating = opponentRatings.reduce((sum, r) => sum + r, 0) / 3;

    // Calculate components
    const baseDelta = getBasePlacementDelta(playerPlace);
    const opponentBonus = calculateOpponentBonus(playerPlace, playerRating, avgOpponentRating);
    const meanReversion = calculateMeanReversion(playerRating, true); // Players in this function have raced
    const edgeScaling = calculateEdgeScaling(playerRating);

    // Apply edge scaling to base delta and opponent bonus
    const scaledChange = (baseDelta + opponentBonus) * edgeScaling;

    // Final rating update (mean reversion applied outside scaling)
    const newRating = playerRating + scaledChange + meanReversion;
    newRatings.push(clampRating(newRating));
  }

  return newRatings;
}

/**
 * Simulate race rating updates without player names
 * @param {number[]} ratings - Array of current ratings
 * @param {number[]} placements - Array of placements 1-4
 * @returns {number[]} New ratings array
 */
export function simulateRace(ratings, placements) {
  const dummyPlayers = ratings.map((_, i) => `Player${i + 1}`);
  return updateAllRatings(dummyPlayers, ratings, placements);
}

/**
 * Calculate player ELO based on tournament history with proper data integrity
 * @param {Array} playerHistory - Tournament history for this player
 * @param {string} playerKey - Player identifier (must contain _player_a or _player_b)
 * @param {Array} allTournamentData - All tournament data for opponent lookups
 * @returns {Object} ELO calculation result
 */
export const calculatePlayerElo = (playerHistory, playerKey, allTournamentData) => {
  // This function is deprecated - ELO is now calculated in Firebase
  // Return default values for compatibility
  console.log(`📊 ${playerKey || 'undefined'}: Using Firebase ELO system`);
  
  return {
    currentElo: ELO_CONFIG.DEFAULT_RATING,
    eloHistory: [],
    tournamentCount: playerHistory?.length || 0
  };
};

// Legacy compatibility exports
export const ELO_CONFIG_LEGACY = {
  STARTING_ELO: ELO_CONFIG.DEFAULT_RATING,
  MIN_ELO: ELO_CONFIG.MIN_RATING,
  MAX_ELO: ELO_CONFIG.MAX_RATING,
};

export const calculateTeamElo = (playerAElo, playerBElo) => {
  return Math.round((playerAElo + playerBElo) / 2);
};

export const getEloTier = (elo) => {
  if (elo >= 900) return { name: 'Champion', color: '#FFD700', icon: '👑' };
  if (elo >= 800) return { name: 'Expert', color: '#C0C0C0', icon: '⭐' };
  if (elo >= 700) return { name: 'Advanced', color: '#CD7F32', icon: '🏆' };
  if (elo >= 600) return { name: 'Intermediate', color: '#4CAF50', icon: '🏁' };
  if (elo >= 400) return { name: 'Beginner', color: '#2196F3', icon: '🏎️' };
  return { name: 'Novice', color: '#9E9E9E', icon: '🏃' };
};

export const getEloConfidence = (tournamentsPlayed) => {
  if (tournamentsPlayed === 0) return 'Unrated';
  if (tournamentsPlayed < 3) return 'Provisional';
  if (tournamentsPlayed < 10) return 'Establishing';
  return 'Established';
};

export { ELO_CONFIG };